import { dlog } from "../shared/debug";

/**
 * Restoration Dashboard (native Collection List panes)
 *
 * The dashboard data now lives in two Webflow Collection Lists bound to the
 * Water Bodies collection — this widget only orchestrates show/hide:
 *
 *   LEFT  overview item  →  data-bop-wb    = Region Key   (the trigger)
 *   RIGHT detail pane    →  data-bop-pane  = Region Key   (the target)
 *   RIGHT site card      →  data-bop-site  = slug/name    (optional scroll target)
 *
 * Behaviour:
 *   1. Map water-body click → open the popup + show that pane
 *   2. Reef-site card (resto page) → open popup + show pane + scroll to the site
 *   3. Left overview click → switch the right pane if the popup is open,
 *      otherwise open the popup at that water body
 *   4. Only one pane is ever displayed (the rest are display:none)
 *   5. Hover sync with the map (both directions):
 *      — map hover → `is-hover` class on matching [data-bop-wb] cards
 *        (style `.is-hover` in Webflow to make the overview shift/highlight)
 *      — hovering a [data-bop-wb] card → that water body glows on the map
 *
 * Inline accordion mode — active when a [data-bop-wb-accordion] container
 * exists on the page (the left-side water body menu next to the map):
 *   — clicking a [data-bop-wb] card expands its [data-bop-wb-details] in place
 *     (is-open on both card and details; siblings collapse) and the map
 *     reframes onto that water body (bop-map:flyTo)
 *   — clicking the open card collapses it and resets the map view
 *   — map water-body click expands the matching card instead of the popup
 *   — [data-bop-compact] elements have their numeric text formatted (70.0M)
 */

// ─── Dashboard show/hide ────────────────────────────

function getDash() { return document.querySelector<HTMLElement>(".section__resto-dashboard"); }
function getRoot() { return document.querySelector<HTMLElement>(".resto-dash__backdrop"); }

function showDashboard(regionKey: string, siteName?: string) {
  const dash = getDash();
  if (!dash) {
    console.warn("[BOP] dashboard element .section__resto-dashboard not found");
    return;
  }
  const root = getRoot() ?? dash; // .resto-dash__backdrop is the full-viewport overlay

  ensureCloseBtn(dash);

  // Un-hide any ancestor above the overlay that Webflow left at display:none.
  let p = root.parentElement;
  while (p && p !== document.body) {
    if (getComputedStyle(p).display === "none") p.style.display = "block";
    p = p.parentElement;
  }

  // Layout, centering, the backdrop look, and the open/close transition are ALL
  // owned by Webflow. JS only drops the overlay into layout, then toggles
  // `is-active` on the next frame so your CSS can transition.
  root.style.display = "";
  if (dash !== root) dash.style.display = "";
  root.onclick = (e) => { if (e.target === root) hideDashboard(); }; // click backdrop to close
  void root.offsetHeight; // commit hidden base state so the transition has a "from"
  root.classList.add("is-active");
  dash.classList.add("is-active");
  lockScroll();

  showPane(regionKey);
  if (siteName) scrollToSite(regionKey, siteName);
}

function hideDashboard() {
  const dash = getDash();
  const root = getRoot() ?? dash;
  if (root) {
    root.classList.remove("is-active");
    dash?.classList.remove("is-active");
    // After the CSS transition fades it out, pull it from layout (kills interaction).
    window.setTimeout(() => {
      if (!root.classList.contains("is-active")) {
        root.style.display = "none";
        if (dash && dash !== root) dash.style.display = "none";
      }
    }, 350);
  }
  unlockScroll();
}

// ─── Pane controller (native Collection List) ───────

function showPane(regionKey: string) {
  if (!regionKey) return;
  document.querySelectorAll<HTMLElement>("[data-bop-pane]").forEach(pane => {
    pane.style.display = pane.getAttribute("data-bop-pane") === regionKey ? "" : "none";
  });
  document.querySelectorAll<HTMLElement>("[data-bop-wb]").forEach(card => {
    card.classList.toggle("is-active", card.getAttribute("data-bop-wb") === regionKey);
  });
}

function scrollToSite(regionKey: string, siteName: string) {
  const pane = document.querySelector<HTMLElement>(`[data-bop-pane="${cssEscape(regionKey)}"]`);
  if (!pane) return;
  const target = norm(siteName);
  if (!target) return;
  const cards = Array.from(
    pane.querySelectorAll<HTMLElement>("[data-bop-site], .restoDash__waterBody-Site, .c-water-site__card")
  );
  for (const card of cards) {
    const nm = norm(card.getAttribute("data-bop-site") ?? card.textContent ?? "");
    if (nm && (nm === target || nm.includes(target) || target.includes(nm))) {
      card.scrollIntoView({ behavior: "smooth", block: "center" });
      card.classList.add("is-flash");
      setTimeout(() => card.classList.remove("is-flash"), 1600);
      break;
    }
  }
}

// ─── Close button (prefers a Webflow-built button; JS fallback otherwise) ──

function ensureCloseBtn(dash: HTMLElement): HTMLElement {
  const host = dash.querySelector<HTMLElement>(".resto-dash__main") ?? dash;
  let btn = dash.querySelector<HTMLElement>("[data-bop-dash-close], .resto-dash__close");
  if (!btn) {
    btn = document.createElement("button");
    btn.setAttribute("data-bop-dash-close", "");
    btn.setAttribute("aria-label", "Close");
    btn.innerHTML = "&times;";
    btn.style.cssText =
      "position:absolute;top:14px;right:14px;z-index:3;width:40px;height:40px;" +
      "border:none;border-radius:999px;background:rgba(8,24,38,0.65);color:#fff;" +
      "font-size:24px;line-height:1;cursor:pointer;display:flex;align-items:center;" +
      "justify-content:center;";
    host.appendChild(btn);
  }
  btn.onclick = (e) => { e.stopPropagation(); hideDashboard(); };
  return btn;
}

// ─── Scroll lock (Lenis-aware) ──────────────────────

type LenisLike = { stop?: () => void; start?: () => void };
function getLenis(): LenisLike | undefined {
  const w = window as unknown as { __lenis?: LenisLike; lenis?: LenisLike };
  return w.__lenis ?? w.lenis;
}

// Block wheel/touch scroll everywhere EXCEPT inside the dashboard window, so the
// page can't scroll behind the modal regardless of whether Lenis is reachable.
let scrollBlocker: ((e: Event) => void) | null = null;

function lockScroll() {
  getLenis()?.stop?.();
  document.documentElement.style.overflow = "hidden";
  document.body.style.overflow = "hidden";
  if (!scrollBlocker) {
    scrollBlocker = (e: Event) => {
      const win = getDash();
      const t = e.target as Node | null;
      if (win && t && (win === t || win.contains(t))) return; // allow scroll inside the window
      e.preventDefault();
    };
    window.addEventListener("wheel", scrollBlocker, { passive: false, capture: true });
    window.addEventListener("touchmove", scrollBlocker, { passive: false, capture: true });
  }
}
function unlockScroll() {
  getLenis()?.start?.();
  document.documentElement.style.overflow = "";
  document.body.style.overflow = "";
  if (scrollBlocker) {
    window.removeEventListener("wheel", scrollBlocker, true);
    window.removeEventListener("touchmove", scrollBlocker, true);
    scrollBlocker = null;
  }
}

// ─── Helpers ────────────────────────────────────────

function norm(s: string) { return s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim(); }

function cssEscape(s: string) {
  return (window.CSS && CSS.escape) ? CSS.escape(s) : s.replace(/["\\]/g, "\\$&");
}

// Map a Waterbody option label (from a reef-site card) → the Region Key used by
// the map, panes, and triggers.
function wbIdFromName(name: string): string | null {
  const n = norm(name);
  if (!n) return null;
  if (n.includes("jamaica")) return "jamaica-bay";
  if (n.includes("hudson")) return "hudson-river";
  if (n.includes("sound") || n === "lis" || n.includes("long island")) return "western-lis";
  if (n.includes("upper")) return "upper-bay";
  if (n.includes("lower") || n.includes("raritan")) return "lower-bay";
  return null;
}

// ─── Inline accordion (left-side water body menu) ───

function getAccordion() {
  return document.querySelector<HTMLElement>("[data-bop-wb-accordion]");
}

function cardKey(card: HTMLElement): string | null {
  return card.getAttribute("data-bop-wb") || card.getAttribute("data-region-key");
}

function accordionCards(acc: HTMLElement): HTMLElement[] {
  return Array.from(acc.querySelectorAll<HTMLElement>("[data-bop-wb], .wb-card[data-region-key]"));
}

function findAccordionCard(key: string): HTMLElement | null {
  const acc = getAccordion();
  if (!acc) return null;
  return accordionCards(acc).find((card) => cardKey(card) === key) ?? null;
}

/** Nearest ancestor that actually scrolls horizontally (the tablet/mobile card
 *  track). Returns null on the desktop vertical layout. */
function horizontalScrollParent(el: HTMLElement): HTMLElement | null {
  let n = el.parentElement;
  while (n) {
    const ox = getComputedStyle(n).overflowX;
    if ((ox === "auto" || ox === "scroll") && n.scrollWidth > n.clientWidth + 1) return n;
    n = n.parentElement;
  }
  return null;
}

/** Tablet/mobile: bring the active card to ~2rem from the card track's left edge.
 *  No-op on desktop (the cards there scroll vertically, not horizontally). */
function snapCardToStart(card: HTMLElement) {
  const scroller = horizontalScrollParent(card);
  if (!scroller) return;
  requestAnimationFrame(() => {
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const delta = card.getBoundingClientRect().left - scroller.getBoundingClientRect().left - 2 * rem;
    if (Math.abs(delta) > 1) scroller.scrollBy({ left: delta, behavior: "smooth" });
  });
}

/* Mobile fullscreen portal: position:fixed can't escape the transformed/sticky
   ancestors Webflow + GSAP put around the accordion, so the nav and section
   headline kept painting OVER the fullscreen card. While open on mobile the
   card is moved to <body> (a comment node marks its home) and returned on
   close — the standard modal escape hatch. */
let portaledCard: HTMLElement | null = null;
let portalHome: Comment | null = null;
function portalCardToBody(card: HTMLElement) {
  if (portaledCard === card) return;
  restorePortaledCard();
  portalHome = document.createComment("wb-card-home");
  card.parentNode?.insertBefore(portalHome, card);
  document.body.appendChild(card);
  portaledCard = card;
}
function restorePortaledCard() {
  if (!portaledCard) return;
  if (portalHome?.parentNode) portalHome.parentNode.insertBefore(portaledCard, portalHome);
  portalHome?.remove();
  portaledCard = null;
  portalHome = null;
}

/** Expand one card (collapse the rest). Pass null to collapse all. */
function setOpenCard(key: string | null) {
  const acc = getAccordion();
  if (!acc) return;
  let openCard: HTMLElement | null = null;
  const cards = accordionCards(acc);
  // A portaled card lives on <body>, outside the accordion scan — keep it in
  // the pass so its open classes are cleared when another card takes over.
  if (portaledCard && !cards.includes(portaledCard)) cards.push(portaledCard);
  for (const card of cards) {
    const open = !!key && cardKey(card) === key;
    if (open) openCard = card;
    // is-wb-open (not is-open): combo names are global in Webflow and the API
    // can't address same-name combos on different parents — unique name required
    card.classList.toggle("is-wb-open", open);
    // Collapsed cards stay compact; only the open one widens (wb-card-expanded)
    card.classList.toggle("wb-card-expanded", open);
    const toggle = card.querySelector<HTMLElement>(".wb-card__toggle");
    if (toggle) toggle.textContent = open ? "–" : "+";
    card.querySelectorAll<HTMLElement>("[data-bop-wb-details]").forEach((d) => {
      d.classList.toggle("is-open", open);
    });
  }
  // Mobile: escape the stacking context for the fullscreen takeover; otherwise
  // (or on close/desktop) make sure the card is back in its home slot.
  if (openCard && window.matchMedia("(max-width: 767px)").matches) {
    portalCardToBody(openCard);
  } else {
    restorePortaledCard();
  }
  // When the cards are a horizontal track (tablet/mobile), pull the freshly
  // opened card to 2rem from the left so it reads as the active one.
  if (openCard && openCard !== portaledCard) snapCardToStart(openCard);
}

function openWaterBody(key: string) {
  setOpenCard(key);
  document.dispatchEvent(new CustomEvent("bop-map:flyTo", { detail: { target: key } }));
}

function closeWaterBody() {
  setOpenCard(null);
  document.dispatchEvent(new CustomEvent("bop-map:reset"));
}

// ─── Inline site accordion (sites within an open water body) ───
//
// Markup contract (built + styled in Webflow, inside each [data-bop-wb-details]):
//   <div data-bop-site-card>                     ← the repeating reef-site card
//     <div data-bop-site-toggle> … head … </div> ← the clickable row
//     <div data-bop-site-details> … media + stats grid + structures … </div>
//   </div>
// Open one site → its [data-bop-site-details] gets `is-open` (+ card `is-site-open`);
// every sibling site in the SAME water body collapses. Style the open/closed
// states in the Designer (e.g. grid-template-rows 0fr→1fr on the details).

/** Open one site card within a scope (collapse its siblings). Pass null to collapse all. */
function setOpenSite(scope: HTMLElement, target: HTMLElement | null) {
  scope.querySelectorAll<HTMLElement>("[data-bop-site-card]").forEach((card) => {
    const open = card === target;
    card.classList.toggle("is-site-open", open);
    const tg = card.querySelector<HTMLElement>("[data-bop-site-toggle] .wb-site-card__toggle");
    if (tg) tg.textContent = open ? "–" : "+";
    // Chevron is an icon embed (not a +/- text node), so give it its own state
    // class. Webflow styles the open state with the simple combo
    // `.wb-site-card__chev.wb-chev-open` (e.g. transform: rotate(90deg)).
    card.querySelectorAll<HTMLElement>("[data-bop-site-chevron]").forEach((c) => {
      c.classList.toggle("wb-chev-open", open);
      c.textContent = open ? "–" : "+";
    });
    // Unique state class (NOT the shared `is-open` combo) so the collapse style
    // can't collide with the region-level details in Webflow's global combo space.
    card.querySelectorAll<HTMLElement>("[data-bop-site-details]").forEach((d) => {
      d.classList.toggle("wb-detail-open", open);
    });
  });
}

/** Find the site card for a given site name within an (already-open) water body card. */
function findSiteCard(regionKey: string, siteName: string): HTMLElement | null {
  const wbCard = findAccordionCard(regionKey);
  const scope = wbCard?.querySelector<HTMLElement>("[data-bop-wb-details]") ?? wbCard;
  if (!scope) return null;
  const target = norm(siteName);
  if (!target) return null;
  for (const card of Array.from(scope.querySelectorAll<HTMLElement>("[data-bop-site-card]"))) {
    const nm = norm(card.getAttribute("data-bop-site") ?? card.textContent ?? "");
    if (nm && (nm === target || nm.includes(target) || target.includes(nm))) return card;
  }
  return null;
}

// Scroll the page up to the map section. Uses Lenis (the site's smooth-scroll)
// when present, else native smooth scroll. Returns false if the anchor is absent.
const MAP_ANCHOR = "#scroll-point-map";
function scrollToMapAnchor(): boolean {
  const anchor = document.querySelector<HTMLElement>(MAP_ANCHOR);
  if (!anchor) return false;
  const lenis = getLenis() as (LenisLike & { scrollTo?: (t: unknown, o?: unknown) => void }) | undefined;
  if (typeof lenis?.scrollTo === "function") lenis.scrollTo(anchor, { offset: 0 });
  else anchor.scrollIntoView({ behavior: "smooth", block: "start" });
  return true;
}

/** Focus a site inside an expanded water-body card: expand it (collapsing
 *  siblings) when the site-accordion markup is present, else just flash it. */
function flashInlineSite(regionKey: string, siteName: string) {
  const card = findSiteCard(regionKey, siteName);
  if (card && card.matches("[data-bop-site-card]")) {
    const scope = card.closest<HTMLElement>("[data-bop-wb-details]") ?? card.parentElement;
    if (scope) setOpenSite(scope, card);
    card.classList.add("is-flash");
    setTimeout(() => card.classList.remove("is-flash"), 1600);
    return;
  }
  // Fallback: no per-site collapse markup — just flash the matching row.
  const acc = getAccordion();
  const wbCard = findAccordionCard(regionKey);
  const scope = wbCard?.querySelector<HTMLElement>("[data-bop-wb-details]") ?? wbCard ?? acc;
  const target = norm(siteName);
  if (!scope || !target) return;
  for (const row of Array.from(scope.querySelectorAll<HTMLElement>("[data-bop-site], .wb-site-card"))) {
    const nm = norm(row.getAttribute("data-bop-site") ?? row.textContent ?? "");
    if (nm && (nm === target || nm.includes(target) || target.includes(nm))) {
      row.classList.add("is-flash");
      setTimeout(() => row.classList.remove("is-flash"), 1600);
      break;
    }
  }
}

/** Open a water body inline, scroll up to the map, and flash the chosen site. */
function openWaterBodyToSite(key: string, siteName?: string) {
  openWaterBody(key);
  if (!scrollToMapAnchor()) {
    // No map anchor on this page — fall back to centering the accordion card.
    findAccordionCard(key)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }
  if (siteName) setTimeout(() => flashInlineSite(key, siteName), 140);
}

/** Format raw CMS numbers ("70000000") in [data-bop-compact] elements → "70.0M". */
function formatCompactCounts() {
  document.querySelectorAll<HTMLElement>("[data-bop-compact]").forEach((el) => {
    const n = parseFloat((el.textContent ?? "").replace(/[^0-9.\-]/g, ""));
    if (isNaN(n)) return;
    if (n >= 1_000_000) el.textContent = `${(n / 1_000_000).toFixed(1)}M`;
    else if (n >= 1_000) el.textContent = `${Math.round(n / 1_000)}K`;
    else el.textContent = n.toLocaleString();
  });
}

/** Map trend option text in [data-bop-trend] elements → arrow + state class. */
function formatTrends() {
  document.querySelectorAll<HTMLElement>("[data-bop-trend]").forEach((el) => {
    const t = (el.textContent ?? "").trim().toLowerCase();
    if (t.startsWith("increas")) { el.textContent = "▲"; el.classList.add("is-up"); }
    else if (t.startsWith("decreas")) { el.textContent = "▼"; el.classList.add("is-down"); }
    else if (t.startsWith("stagn")) { el.textContent = "–"; el.classList.add("is-flat"); }
    else el.textContent = ""; // unset trend → show nothing
  });
}

function normalizeInlineSiteTitles() {
  const acc = getAccordion();
  if (!acc) return;
  for (const card of accordionCards(acc)) {
    const count = card.querySelectorAll("[data-bop-site-card]").length;
    card.querySelectorAll<HTMLElement>(".wb-sites__title").forEach((title) => {
      title.textContent = `Reef sites · ${count}`;
    });
  }
}

// ─── Listeners ──────────────────────────────────────

/* Mobile override (Skye-approved manual CSS overwrite, 2026-08-04): on mobile
   landscape and below, the expanded water-body card takes over the screen
   instead of colliding with the map headline — full-bleed, own scroll, sticky
   header so the collapse toggle stays reachable (tap head to close). Visual
   styling otherwise stays Webflow-owned. */
const WB_MOBILE_CSS = `
@media (max-width: 767px){
.wb-card.wb-card-expanded{position:fixed;top:0;left:0;right:0;bottom:0;width:100vw;height:100dvh;max-height:100dvh;margin:0;border-radius:0;z-index:2147480000;overflow-y:auto;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;padding-bottom:calc(24px + env(safe-area-inset-bottom,0px));background:#141314}
.wb-card.wb-card-expanded .wb-card__head{position:sticky;top:0;z-index:2;background:#141314;border-bottom:1px solid rgba(255,255,255,0.08);padding:14px 16px}
.wb-card.wb-card-expanded [data-bop-wb-details]{margin-top:1rem}
/* The decorative header gradient uses a negative margin to layer over the
   cards on desktop; on mobile it collides with them — stack it normally. */
.gradient-behind-restoheader{margin:0 !important;position:relative;z-index:1}
}`;
let wbMobileCssInjected = false;
function injectWbMobileCss() {
  if (wbMobileCssInjected) return;
  wbMobileCssInjected = true;
  const st = document.createElement("style");
  st.textContent = WB_MOBILE_CSS;
  document.head.appendChild(st);
}

function init() {
  injectWbMobileCss();
  // Hide the overlay (and the window, in case they're separate) on load.
  const root = getRoot() ?? getDash();
  const dash = getDash();
  if (root) { root.style.display = "none"; root.classList.remove("is-active"); }
  if (dash && dash !== root) { dash.style.display = "none"; dash.classList.remove("is-active"); }

  // Collapse the Collection List to a single visible pane up front (so it never
  // renders all panes stacked, even if the dashboard is shown inline).
  const firstPane = document.querySelector<HTMLElement>("[data-bop-pane]");
  if (firstPane) showPane(firstPane.getAttribute("data-bop-pane") ?? "");

  formatCompactCounts();
  formatTrends();
  normalizeInlineSiteTitles();
  setOpenCard(null);

  // Reef-site chevrons ship as empty icon embeds — paint a default "+" so the
  // control is visible (the open state flips it to "–" in setOpenSite).
  document.querySelectorAll<HTMLElement>("[data-bop-site-chevron]").forEach((c) => {
    if (!c.textContent?.trim()) c.textContent = "+";
  });

  // The site's smooth scroll (Lenis) hijacks the wheel globally, which stops the
  // overflow:auto cards panel from scrolling natively on the published page
  // (it works in the Designer only because Lenis isn't running there). Tagging
  // the panel tells Lenis to release wheel/touch inside it so it scrolls itself.
  getAccordion()?.setAttribute("data-lenis-prevent", "true");

  // Map click → expand the inline accordion card if present, else open popup
  document.addEventListener("bop-map:waterBodyClicked", ((e: Event) => {
    const d = (e as CustomEvent).detail;
    if (!d?.waterBodyId) return;
    if (getAccordion()) openWaterBody(d.waterBodyId);
    else showDashboard(d.waterBodyId);
  }) as EventListener);

  // Map hover → mark matching overview cards (page-side lists styled in Webflow)
  document.addEventListener("bop-map:waterBodyHovered", ((e: Event) => {
    const key = (e as CustomEvent).detail?.waterBodyId ?? null;
    document.querySelectorAll<HTMLElement>("[data-bop-wb], .wb-card[data-region-key]").forEach(card => {
      card.classList.toggle("is-hover", !!key && cardKey(card) === key);
    });
  }) as EventListener);

  // Overview card hover → glow that water body on the map
  document.addEventListener("mouseover", (e) => {
    const card = (e.target as HTMLElement | null)?.closest?.("[data-bop-wb], .wb-card[data-region-key]") as HTMLElement | null;
    if (!card) return;
    const from = (e as MouseEvent).relatedTarget as HTMLElement | null;
    if (from && from.closest?.("[data-bop-wb], .wb-card[data-region-key]") === card) return; // already inside this card
    document.dispatchEvent(new CustomEvent("bop-map:hover", {
      detail: { waterBodyId: cardKey(card) },
    }));
  });
  document.addEventListener("mouseout", (e) => {
    const card = (e.target as HTMLElement | null)?.closest?.("[data-bop-wb], .wb-card[data-region-key]") as HTMLElement | null;
    if (!card) return;
    const to = (e as MouseEvent).relatedTarget as HTMLElement | null;
    if (to && card.contains(to)) return; // still inside this card
    document.dispatchEvent(new CustomEvent("bop-map:hover", { detail: { waterBodyId: null } }));
  });

  document.addEventListener("click", (e) => {
    const el = e.target as HTMLElement | null;

    // Reef-site card inside an open water body → expand its details,
    // collapsing the other sites in the same water body (single-open).
    // The WHOLE card row is tappable (not just the +/- toggle); clicks inside
    // the expanded details (links, media, stats) never collapse it.
    const siteToggle = el?.closest?.("[data-bop-site-toggle]") as HTMLElement | null;
    const siteCardHit = el?.closest?.("[data-bop-site-card]") as HTMLElement | null;
    if (siteToggle || (siteCardHit && !el?.closest?.("[data-bop-site-details]"))) {
      const card = siteToggle?.closest<HTMLElement>("[data-bop-site-card]") ?? siteCardHit;
      const scope = card?.closest<HTMLElement>("[data-bop-wb-details]") ?? card?.parentElement ?? null;
      dlog("[BOP dash] site tap", {
        viaToggle: !!siteToggle, viaCard: !!siteCardHit,
        card: !!card, scope: !!scope,
        wasOpen: card?.classList.contains("is-site-open"),
      });
      if (card && scope) {
        if (card.classList.contains("is-site-open")) setOpenSite(scope, null);
        else setOpenSite(scope, card);
      }
      return; // never bubble up to toggle the parent water body
    }

    // Left overview water-body card
    const wbTrigger = el?.closest?.("[data-bop-wb], .wb-card[data-region-key]") as HTMLElement | null;
    if (wbTrigger) {
      const key = cardKey(wbTrigger);
      if (!key) return;

      // Inline accordion card → expand/collapse in place + reframe the map.
      // Clicks inside the expanded details (site rows, links) don't toggle.
      // A mobile-fullscreen card is portaled to <body> (no accordion ancestor)
      // but must keep accordion behavior — otherwise it can't be closed.
      if (wbTrigger.closest("[data-bop-wb-accordion]") || wbTrigger === portaledCard) {
        if (el?.closest?.("[data-bop-wb-details]")) return;
        if (wbTrigger.classList.contains("is-wb-open")) closeWaterBody();
        else openWaterBody(key);
        return;
      }

      // Popup overview card → switch pane / open popup
      const root = getRoot() ?? getDash();
      const isOpen = !!root && root.classList.contains("is-active");
      if (isOpen) showPane(key);
      else showDashboard(key);
      return;
    }

    // Reef-site CMS card (resto page) → engage the inline water-body experience
    // (expand its accordion card, reframe the map, focus this site). Falls back
    // to the retired popup only when no inline accordion is on the page.
    // Carries: data-bop-open, data-bop-site-wb (waterbody name), data-bop-site (site name).
    const open = el?.closest?.("[data-bop-open]") as HTMLElement | null;
    if (open) {
      e.preventDefault();
      const wbId = wbIdFromName(open.getAttribute("data-bop-site-wb") ?? "");
      const siteName = open.getAttribute("data-bop-site") ?? "";
      if (!wbId) {
        console.warn("[BOP] reef card: unresolved waterbody", open.getAttribute("data-bop-site-wb"));
        return;
      }
      if (getAccordion()) openWaterBodyToSite(wbId, siteName || undefined);
      else showDashboard(wbId, siteName || undefined);
    }
  });

  // ESC → close dashboard
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") hideDashboard();
  });

  dlog("[BOP] Dashboard ready (native panes)");
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
